import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, unref as _unref } from "vue"

const _hoisted_1 = { class: "decision-container" }
const _hoisted_2 = {
  key: 0,
  class: "decision-container__delete-icon"
}
const _hoisted_3 = { class: "decision-container__drag-icon handle" }
const _hoisted_4 = { class: "decision-container__clause" }
const _hoisted_5 = { class: "decision-container__condition" }
const _hoisted_6 = {
  key: 0,
  class: "help is-danger"
}
const _hoisted_7 = {
  key: 1,
  class: "help is-danger"
}
const _hoisted_8 = { class: "decision-container__transition" }
const _hoisted_9 = { class: "decision-container__transition-picker" }
const _hoisted_10 = {
  key: 0,
  class: "help is-danger"
}
const _hoisted_11 = {
  key: 1,
  class: "help is-danger"
}

import ConditionGroupBuilder, { ConditionGroupUpdate } from '@/shared/components/conditions/ConditionGroupBuilder.vue';
import TagBox from '@/shared/components/TagBox.vue';
import TransitionPicker from '@/features/bots/components/designer/TransitionPicker.vue';
import DragIcon from '@/shared/components/DragIcon.vue';
import { TransitionViewModel } from '@/features/bots/models/designer/transitionViewModel';
import { DecisionViewModel } from '@/features/bots/models/designer/decisionViewModel';
import { required, minLength } from '@vuelidate/validators';
import { InputVariableViewModel } from '../../models/designer';
import { ConditionDataType, ConditionPickerOption } from '@/shared/components/conditions/models/conditions';
import { computed, nextTick, onMounted, PropType, ref, watch } from 'vue';
import { useStore } from 'vuex';
import useVuelidate from '@vuelidate/core';
import VuelidateUtil from '@shared/util/vuelidateUtil';
import {
  DecisionConditionsAttributeType,
  useDecisionConditions,
} from '@/features/bots/components/designer/Conditions/useDecisionConditions';


export default /*@__PURE__*/_defineComponent({
  __name: 'Decision',
  props: {
  decision: {
    type: Object as PropType<DecisionViewModel>,
    required: true,
  },
  canDelete: {
    type: Boolean,
    default: false,
  },
},
  emits: ["updated", "deleted"],
  setup(__props, { emit: __emit }) {

const props = __props;

const store = useStore();
const flowVariables = computed<InputVariableViewModel[]>(() => store.getters.flowVariables);
const isTransitionPayloadInvalid = (payload: string) =>
  computed(() => store.getters.isTransitionPayloadInvalid(payload));

const decisionConditionsManager = useDecisionConditions();

const isInvalidCondition = ref(false);
const providedDecision = ref<DecisionViewModel>({
  conditions: {
    type: 'and',
    conditions: [],
  },
  transition: {
    payload: undefined,
    type: undefined,
  },
});

const validations = {
  providedDecision: {
    conditions: {
      conditions: {
        required,
        minLength: minLength(1),
        conditionValid() {
          return !isInvalidCondition.value;
        },
      },
    },
    transition: {
      required,
      type: {
        required,
      },
      payload: {
        payloadValid(payload: string) {
          return !payload || !isTransitionPayloadInvalid(payload).value;
        },
      },
    },
  },
};

const isConditionsRequiredValidationInvalid = computed(() =>
  VuelidateUtil.isRequiredInvalid($v, (v) => v.providedDecision.conditions.conditions),
);
const isConditionsInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, (v) => v.providedDecision.conditions.conditions, 'conditionValid'),
);
const $v = useVuelidate(validations, { providedDecision });
const isTransitionValidationInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, (v) => v.providedDecision.transition),
);
const isTransitionPayloadValidationInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, (v) => v.providedDecision.transition.payload, 'payloadValid'),
);

onMounted(() => {
  providedDecision.value = props.decision;
  checkValidTransition();
  setUpConditions();
});

watch(
  flowVariables,
  () => {
    setUpConditions();
    providedDecision.value = JSON.parse(JSON.stringify(providedDecision.value));
  },
  { immediate: true, deep: true },
);

const emit = __emit;

function conditionUpdated(update: ConditionGroupUpdate) {
  isInvalidCondition.value = update.invalid;
  const clone = JSON.parse(JSON.stringify(providedDecision.value)); // to prevent updating the providedDecision which would kick off processing in child
  clone.conditions = update.conditionGroup;
  emit('updated', clone, $v.value.providedDecision.$invalid);
}

function deleteDecision() {
  emit('deleted');
}

function transitionSelected(transition: TransitionViewModel) {
  providedDecision.value.transition = transition;
  emit('updated', providedDecision.value, $v.value.providedDecision.$invalid);
}

function checkValidTransition() {
  if (!providedDecision.value.transition) {
    $v.value.providedDecision.transition!.$touch();
    nextTick(() => emit('updated', providedDecision.value, $v.value.providedDecision.$invalid));
  }
}

function setUpConditions() {
  decisionConditionsManager.setContextualConditions([
    ...flowVariables.value.map((flowVar) => {
      return {
        id: `flow.${flowVar.name}`,
        title: flowVar.name,
        type: ConditionDataType.Text,
        icon: 'FlowVariableIcon',
      } as ConditionPickerOption;
    }),
    {
      id: DecisionConditionsAttributeType.TimeOfDay,
      title: 'Time of day',
      type: ConditionDataType.DateTime,
      icon: 'DurationIcon',
    },
  ]);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.canDelete)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          (_openBlock(), _createElementBlock("svg", {
            onClick: deleteDecision,
            class: "trash-icon"
          }, _cache[0] || (_cache[0] = [
            _createElementVNode("use", {
              "xmlns:xlink": "http://www.w3.org/1999/xlink",
              "xlink:href": "#trash"
            }, null, -1)
          ])))
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(DragIcon, {
        color: "#A9A9A9",
        class: "icon-1-5x drag-icon"
      })
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createVNode(TagBox, { value: "if" }),
      _createElementVNode("div", _hoisted_5, [
        _createVNode(ConditionGroupBuilder, {
          value: providedDecision.value.conditions,
          manager: _unref(decisionConditionsManager),
          disabled: false,
          context: "condition",
          onUpdated: conditionUpdated
        }, null, 8, ["value", "manager"]),
        (isConditionsInvalid.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_6, "Must not contain an invalid condition"))
          : (isConditionsRequiredValidationInvalid.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_7, "Must configure a condition"))
            : _createCommentVNode("", true)
      ])
    ]),
    _createElementVNode("div", _hoisted_8, [
      _createVNode(TagBox, { value: "then" }),
      _createElementVNode("div", _hoisted_9, [
        _createVNode(TransitionPicker, {
          transition: providedDecision.value.transition,
          onTransitionSelected: transitionSelected
        }, null, 8, ["transition"]),
        (isTransitionPayloadValidationInvalid.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_10, "Must not contain an invalid transition"))
          : (isTransitionValidationInvalid.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_11, "Must select a transition"))
            : _createCommentVNode("", true)
      ])
    ])
  ]))
}
}

})